/* @flow */

import * as React from "react";
import { IntlProvider, intlShape } from "react-intl";
import { PhraseProject, Translations } from "./i18nDataBuilder";
import { formats, locale, messagesForAllProjects } from "./i18nDataProvider";

export function getLocale() {
    return locale;
}

// This is produced for refenrential equality constraints considerations
const intlPropsPerPhraseProject = Object.fromEntries(
    Object.entries(messagesForAllProjects).map(([project, translations]) => [
        project,
        {
            locale: locale,
            formats: formats,
            messages: translations
        }
    ])
);

export function getIntlPropsForPhraseProject(phraseProject?: PhraseProject) {
    try {
        phraseProject = phraseProject || "EXPLORE";
        if (phraseProject in intlPropsPerPhraseProject) {
            return {
                ...intlPropsPerPhraseProject[phraseProject]
            };
        }
    } catch {
        // Caught; Will recover by means of default return
    }
    return {
        ...intlPropsPerPhraseProject.EMPTY
    };
}

export function getMessages(phraseProject?: PhraseProject) {
    const intlProps = getIntlPropsForPhraseProject(phraseProject);
    return intlProps.messages;
}

export function getFilteredMessages(phraseProject: PhraseProject, neededTranslationMessages: string[]) {
    let messages: Translations = {};

    try {
        messages = getMessages(phraseProject) || {};
    } catch {
        messages = {};
    }

    return Object.fromEntries(
        neededTranslationMessages.map((translationKey) => {
            const translationWithFallback = messages[translationKey] || translationKey;
            return [translationKey, translationWithFallback];
        })
    );
}

const intlProviderPerPhraseProject = Object.fromEntries(
    Object.entries(intlPropsPerPhraseProject).map(([project, intlProps]) => [project, new IntlProvider(intlProps, {})])
);

export function getIntlProviderForPhraseProject(phraseProject?: PhraseProject) {
    phraseProject = phraseProject || "EXPLORE";
    if (phraseProject in intlProviderPerPhraseProject) {
        return intlProviderPerPhraseProject[phraseProject];
    }
    return intlProviderPerPhraseProject.EMPTY;
}

function getDisplayName(Component: React.ComponentType<any>): string {
    return Component.displayName || (typeof Component === "function" ? Component.name : "");
}

export function wrappedDisplayName(wrapperName: string, Component: React.ComponentType<any>): string {
    return wrapperName + "(" + getDisplayName(Component) + ")";
}

export function withIntlRoot<T extends {}>(
    WrappedComponent: React.ComponentType<T>,
    phraseProject?: PhraseProject
): React.ComponentType<
    Pick<
        T,
        Exclude<
            keyof T,
            keyof {
                intl: any;
            }
        >
    >
> {
    class Wrapper extends React.Component<T> {
        constructor(props: T) {
            super(props);
        }
        static getDefaultProps() {
            return getIntlProviderForPhraseProject(phraseProject)?.getChildContext() || {};
        }
        getChildContext() {
            return getIntlProviderForPhraseProject(phraseProject)?.getChildContext() || {};
        }
        render() {
            return <WrappedComponent {...this.props} />;
        }
        static contextTypes = {
            intl: intlShape.isRequired
        };
        static childContextTypes = {
            intl: intlShape.isRequired
        };
        static displayName = wrappedDisplayName("withIntlRoot", WrappedComponent);
    }
    return Wrapper as any;
}

/* jsxWithInl */

export function jsxWithIntl(
    content: any,
    phraseProject?: PhraseProject,
    filteredMessages?: Translations
): React.ReactNode {
    const intlProps = getIntlPropsForPhraseProject(phraseProject);
    if (filteredMessages) {
        intlProps.messages = filteredMessages;
    }
    return <IntlProvider {...intlProps}>{content}</IntlProvider>;
}

/* IntlMixins per PhraseProject */
const intlMixinPerPhraseProject = Object.fromEntries(
    Object.entries(intlProviderPerPhraseProject).map(([project, intlProvider]) => {
        const IntlRootMixin = {
            contextTypes: {
                intl: intlShape.isRequired
            },
            childContextTypes: {
                intl: intlShape.isRequired
            },
            getDefaultProps() {
                return intlProvider?.getChildContext() || {};
            },
            getChildContext() {
                return intlProvider?.getChildContext() || {};
            }
        };
        return [project, IntlRootMixin];
    })
);

export function getIntlMixinForPhraseProject(phraseProject?: PhraseProject) {
    phraseProject = phraseProject || "EXPLORE";
    if (phraseProject in intlMixinPerPhraseProject) {
        return intlMixinPerPhraseProject[phraseProject];
    }
    return intlMixinPerPhraseProject.EMPTY;
}

/* IntlFormatting per PhraseProject */
const intlFormattingPerPhraseProject = Object.fromEntries(
    Object.entries(intlMixinPerPhraseProject).map(([project, intlMixin]) => {
        const { intl } = intlMixin?.getChildContext() || {};
        const intlFormatting = {
            formatMessage: intl.formatMessage,
            formatNumber: intl.formatNumber
        };
        return [project, intlFormatting];
    })
);

export function getIntlFormatingForPhraseProject(phraseProject?: PhraseProject) {
    phraseProject = phraseProject || "EXPLORE";
    if (phraseProject in intlFormattingPerPhraseProject) {
        return intlFormattingPerPhraseProject[phraseProject];
    }
    return intlFormattingPerPhraseProject.EMPTY;
}
